/**
 * The marketplace's shared item shape and tile renderer.
 *
 * Weapon finishes, charms, sprays, emotes, player cards and titles used to be four (soon six)
 * differently-shaped grids, each with its own tile function repeating the same head/description
 * /footer skeleton with small variations. `MarketItem` is the one shape every one of those
 * categories normalizes into, and `marketTile()` is the one renderer that draws it — the same
 * tile shape everywhere reads as one store, not six lists stapled together (Arturs, 2026-09-16:
 * "красивый UI маркетплейса").
 *
 * Operators (character skins) are NOT normalized into this shape — that grid is the shop's main
 * stock and has its own tall portrait plate (`shop-route.ts`'s `shopTile`), built from the same
 * pieces exported here (ribbon, wash, stamp, price chip, gold buy). This file is for everything
 * else.
 */
import { badge, button, icon, type ButtonOptions, type IconName } from '../ui';
import { formatNumber, t } from '../../i18n';

export type MarketCategoryId =
  | 'weaponSkins'
  | 'charms'
  | 'sprays'
  | 'emotes'
  | 'playerCards'
  | 'titles'
  /**
   * Not a catalogue — the player's own bought items, offered back at the sell-back rate.
   *
   * It rides the same rail and the same tile because owning and pricing are one conversation:
   * the Locker used to be a separate screen for this, and a screen whose whole content is "the
   * things you already bought, with a price on them" is the marketplace looking the other way
   * (`design/gdd/meta-screens.md`).
   */
  | 'sell';

export type MarketRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface MarketPrice {
  amount: number;
  currency: 'SOFT' | 'PREMIUM';
}

export interface MarketAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  onClick: () => void;
}

export interface MarketItem {
  /** Unique across the WHOLE marketplace, not just its own category — `weaponSkin-10003`,
   * `charm-4` — so the curated "All" view can key every tile without collisions. */
  key: string;
  category: MarketCategoryId;
  name: string;
  description: string;
  /** Pre-built art node — a swatch, a pendant, a plate, a glyph. Categories differ enough in
   * what "a picture of this item" means that building it is each adapter's own job; this file
   * only lays the art into the tile's frame. */
  art: HTMLElement;
  /** `null` for an item with nothing to buy (an EARN-only entry, e.g. a level/rank title) —
   * `requirement` carries the free route instead. */
  price: MarketPrice | null;
  owned: boolean;
  equipped: boolean;
  affordable: boolean;
  /** The free route's label, e.g. "Level 10" — shown where the price would sit when `price`
   * is `null`. */
  requirement?: string;
  /** The tile's one action button — omitted for a pure EARN row with nothing to press. */
  action?: MarketAction;
  /** A quiet footer note in place of an action — "equip in armoury", "or Level 10". */
  hint?: string;
  /** A secondary control beside the action, e.g. the emote preview button. Never the tile's
   * only control — always alongside `action` or `hint`. */
  extra?: HTMLElement;
}

/** Presentation tiering only, same posture as `shopTile`'s own `rarityFor` in `shop-route.ts`:
 * the catalogue has no rarity concept, this only maps "how much this costs" onto the same
 * rarity-gradient utilities the operator grid uses, so the whole marketplace reads as one
 * value scale rather than the character skins being the only category that looks premium. */
export function marketRarity(price: MarketPrice | null): MarketRarity {
  if (!price) return 'common';
  if (price.currency === 'SOFT') return price.amount >= 800 ? 'rare' : 'common';
  return price.amount >= 1500 ? 'legendary' : price.amount >= 700 ? 'epic' : 'rare';
}

/** The ribbon's own glow, per tier — a drop-shadow filter because `rarity-*` already owns the
 * element's `box-shadow` and two utilities on one property is a stylesheet-order coin toss. */
const RARITY_GLOW: Record<MarketRarity, string> = {
  common: '[filter:drop-shadow(0_0_5px_var(--color-rarity-common-bright))]',
  rare: '[filter:drop-shadow(0_0_6px_var(--color-rarity-rare-bright))]',
  epic: '[filter:drop-shadow(0_0_7px_var(--color-rarity-epic-bright))]',
  legendary: '[filter:drop-shadow(0_0_8px_var(--color-rarity-legendary-bright))]',
};

/** The light behind the art in a tile's well: the tier colour rising from the floor. */
const RARITY_WASH: Record<MarketRarity, string> = {
  common: 'var(--color-rarity-common)',
  rare: 'var(--color-rarity-rare)',
  epic: 'var(--color-rarity-epic)',
  legendary: 'var(--color-rarity-legendary)',
};

/** A glowing rarity ribbon — the one place `rarity-*` is worn, on a small swatch. */
export function rarityRibbon(rarity: MarketRarity): HTMLElement {
  const el = document.createElement('span');
  el.className = `rarity-${rarity} absolute top-1.5 left-1/2 -translate-x-1/2 z-[1] h-2 w-14 rounded-full ${RARITY_GLOW[rarity]}`;
  el.dataset.rarity = rarity;
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/** The tier's light, laid behind a tile's art. */
export function rarityWash(rarity: MarketRarity): HTMLElement {
  const el = document.createElement('span');
  el.className = 'absolute inset-0 opacity-45 pointer-events-none';
  el.style.background = `radial-gradient(ellipse 80% 70% at 50% 100%, ${RARITY_WASH[rarity]}, transparent 72%)`;
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/** The green tick stamped on a thing the player already has. `label` is its accessible name —
 * the tick replaced the word OWNED on screen, so the word moves here. */
export function ownedStamp(label: string): HTMLElement {
  const stamp = icon('done', { size: 30, className: 'kit-pop absolute top-1 right-1 z-[1] [filter:drop-shadow(0_2px_3px_rgba(0,0,0,0.7))]' });
  stamp.removeAttribute('aria-hidden');
  stamp.alt = label;
  return stamp;
}

/** A price in its slot: the coin or gem rides the left end of a `kit-chip`, the figure in game
 * lettering. Same chip everywhere a price appears, so a price scans as a picture. */
export function priceChip(currency: MarketPrice['currency'], text: string): HTMLElement {
  const chip = document.createElement('span');
  chip.className = 'kit-chip [--kh:30px] shrink-0';
  chip.append(
    icon(currencyIcon(currency), { size: 22, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }),
  );
  const value = document.createElement('span');
  value.className = 'kit-label font-heading text-[14px] tabular-nums';
  value.textContent = text;
  chip.append(value);
  return chip;
}

/** The painted BUY: gold, because it spends currency. `button()` has no gold variant (ui.ts is
 * the kit's, not ours), but `plate-key` reads `data-plate`, so the face is switched here. */
export function buyButton(label: string, opts: ButtonOptions = {}): HTMLButtonElement {
  const btn = button(label, { ...opts, variant: 'primary' });
  btn.dataset.plate = 'gold';
  return btn;
}

/**
 * One grid class shared by every category pane and the curated preview rows — the visual proof
 * that this is one marketplace grid, not a different layout per category.
 *
 * ONE column on a phone, not two. Two columns at 375px gave each tile about 170px, and a tile
 * that narrow truncated its own name to "FIEL…" and "GLAC…" — a storefront where the player
 * cannot read what is for sale. The reference sheet (`design/gdd/reference/4-store.jpg`) runs
 * these as wide rows for exactly that reason, and its widest break is five across, which is
 * where this tops out too.
 */
export const MARKET_GRID_CLASS =
  'grid gap-4 grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';

/**
 * The operator grid — the same breaks from `lg` up, but TWO columns on a phone.
 *
 * An operator card is art-led: a portrait figure with a short line under it, and two of them
 * fit a 375px screen comfortably. The compact tiles above cannot do that (their names truncate
 * below about 200px), which is the entire reason these are two class strings and not one.
 */
export const OPERATOR_GRID_CLASS =
  'grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';

function currencyIcon(currency: MarketPrice['currency']): IconName {
  return currency === 'PREMIUM' ? 'premium' : 'credits';
}

/** Shared look of a square tint swatch — rounded, inked rim, a lit top edge. */
const SWATCH_CLASS =
  'shrink-0 rounded-[5px] border-2 border-black/60 shadow-[inset_0_2px_0_rgba(255,255,255,0.35),0_2px_3px_rgba(0,0,0,0.6)]';

/** A round tint swatch — the shape a charm's pendant and a spray's fallback both want. */
export function tintDot(hex: string, opts: { size?: number; glow?: string } = {}): HTMLElement {
  const el = document.createElement('span');
  const size = opts.size ?? 40;
  el.className = 'shrink-0 rounded-full border-2 border-black/50';
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = hex;
  if (opts.glow) el.style.boxShadow = `0 0 10px 2px ${opts.glow}`;
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/**
 * An item's own picture — the charm's photograph, not a coloured dot standing in for it.
 *
 * `onerror` removes the image rather than leaving a broken glyph: art ships per SKU and a
 * catalogue can name one before its picture lands, which is a missing picture and not a
 * missing item. The caller's fallback (a tint dot) is passed in so this file does not have to
 * know what each category falls back TO.
 *
 * 56px, not 44. Half the charm art hangs from a split ring, so the object itself occupies the
 * lower half of its own square — at 44 a silver wolf's head was a speck beside a bottle cap
 * that filled its box. The box is sized for the thinnest item in the set, not the fattest.
 */
export function itemArt(src: string, fallback: () => HTMLElement, size = 56): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'shrink-0 inline-flex items-center justify-center';
  wrap.style.width = `${size}px`;
  wrap.style.height = `${size}px`;
  const img = document.createElement('img');
  img.src = src;
  img.alt = '';
  img.draggable = false;
  img.className = 'w-full h-full object-contain select-none';
  img.addEventListener('error', () => { wrap.replaceChildren(fallback()); }, { once: true });
  wrap.append(img);
  wrap.setAttribute('aria-hidden', 'true');
  return wrap;
}

/** A weapon finish's swatch: a tint in a bevelled chip, lit from above like the enamel it stands for. */
export function tintSquare(hex: string, opts: { size?: number } = {}): HTMLElement {
  const el = document.createElement('span');
  const size = opts.size ?? 32;
  el.className = SWATCH_CLASS;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = hex;
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/**
 * A tinted glyph — a spray's own picture. `/game/ui/cards/<glyph>.png` is authored as a single
 * flat shape on transparency, meant to be recoloured rather than shipped once per tint, so the
 * glyph is drawn as a CSS mask (the shape) filled with `tint` (the colour) rather than as an
 * `<img>` — the same "one asset, painted per entry" move `applyFinishSwatch` makes for a weapon
 * finish, done in CSS because there is no live paint target here.
 *
 * Degrades to a plain tinted square if the glyph 404s (art not shipped yet) — checked with an
 * offscreen probe `<img>` because `mask-image` itself has no load/error event to hook.
 */
export function spraySwatch(
  glyph: string,
  tintHex: string,
  opts: { size?: number; className?: string } = {},
): HTMLElement {
  const size = opts.size ?? 40;
  const el = document.createElement('span');
  // `inline-block` rather than nothing: this is laid into a flex row in the marketplace, where
  // the default `inline` would have been stretched anyway, but the wardrobe prepends it INSIDE a
  // button, where an inline span ignores width and height and the swatch collapses to nothing.
  // `className` is for that caller's own spacing — kept through the mask swap below, which used
  // to reassign `className` wholesale and silently dropped whatever a caller had added.
  const extra = opts.className ? ` ${opts.className}` : '';
  el.className = `${SWATCH_CLASS} inline-block${extra}`;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = tintHex;
  el.setAttribute('aria-hidden', 'true');

  const url = `/game/ui/cards/${glyph}.png`;
  const probe = new Image();
  probe.addEventListener(
    'load',
    () => {
      el.style.setProperty('-webkit-mask-image', `url(${url})`);
      el.style.setProperty('mask-image', `url(${url})`);
      el.style.setProperty('-webkit-mask-size', 'contain');
      el.style.setProperty('mask-size', 'contain');
      el.style.setProperty('-webkit-mask-repeat', 'no-repeat');
      el.style.setProperty('mask-repeat', 'no-repeat');
      el.style.setProperty('-webkit-mask-position', 'center');
      el.style.setProperty('mask-position', 'center');
      // The plate becomes the glyph: the rim and the border belonged to the square standing
      // in for it, and drawn around a masked shape they are a frame around nothing.
      el.className = `shrink-0 inline-block${extra}`;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.background = tintHex;
    },
    { once: true },
  );
  probe.src = url;
  return el;
}

export function marketPriceLabel(price: MarketPrice): string {
  return formatNumber(price.amount);
}

/**
 * The one tile renderer for every non-operator marketplace category: a pressable steel plate
 * holding a well with the item's art big and lit by its rarity, the name in game lettering, and
 * one footer — price chip and a painted button. Owned things wear a green tick and the plate's
 * teal rim.
 */
export function marketTile(item: MarketItem): HTMLElement {
  const rarity = marketRarity(item.price);

  const el = document.createElement('div');
  el.dataset.market = item.key;
  el.className = [
    'kit-plate kit-plate-hit [--kp-rim:14px] [--kp-pad:12px] h-full flex flex-col gap-2.5 min-w-0',
    item.owned || item.equipped ? 'kit-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // The art is built per category at its own small size; scaled up here so every category shows
  // its item large without each adapter knowing the tile's frame.
  const well = document.createElement('div');
  well.className = 'kit-well relative flex items-center justify-center h-28 overflow-hidden';
  const art = document.createElement('div');
  art.className = 'relative shrink-0 flex items-center justify-center scale-[1.7] [filter:drop-shadow(0_4px_4px_rgba(0,0,0,0.6))]';
  art.append(item.art);
  well.append(rarityWash(rarity), rarityRibbon(rarity), art);
  if (item.owned || item.equipped) well.append(ownedStamp(t('common.owned')));
  el.append(well);

  // `break-words` rather than `truncate`: a storefront cannot afford to hide half a name.
  const name = document.createElement('span');
  name.className = 'kit-label font-heading uppercase text-[15px] leading-tight text-center break-words';
  name.textContent = item.name;
  el.append(name);

  if (item.description) {
    const description = document.createElement('span');
    description.className = 'text-[11px] text-text-dim leading-snug text-center line-clamp-2';
    description.textContent = item.description;
    el.append(description);
  }

  const foot = document.createElement('div');
  foot.className = 'mt-auto flex flex-wrap items-center justify-center gap-2';
  if (item.price) foot.append(priceChip(item.price.currency, marketPriceLabel(item.price)));
  else if (item.requirement) {
    const need = document.createElement('span');
    need.className = 'kit-chip [--kh:30px] shrink-0';
    need.append(icon('locked', { size: 22, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }));
    const text = document.createElement('span');
    text.className = 'kit-label font-heading text-[12px]';
    text.textContent = item.requirement;
    need.append(text);
    foot.append(need);
  }

  const controls = document.createElement('div');
  controls.className = 'flex items-center gap-1.5 shrink-0';
  if (item.extra) controls.append(item.extra);
  if (item.action) {
    const opts = {
      className: 'px-4 text-[12px] shrink-0',
      disabled: item.action.disabled,
      onClick: item.action.onClick,
    };
    // Spending currency is the gold key; equip / remove / "need gems" are not a purchase.
    const spends = !item.owned && item.price !== null && (item.action.variant ?? 'primary') === 'primary';
    controls.append(spends ? buyButton(item.action.label, opts) : button(item.action.label, { ...opts, variant: item.action.variant ?? 'primary' }));
  } else if (item.hint) {
    const hint = document.createElement('span');
    hint.className = 'text-[11px] text-text-dim';
    hint.textContent = item.hint;
    controls.append(hint);
  }
  if (item.equipped) controls.append(badge(t('shop.market.worn'), 'gold'));
  if (controls.children.length > 0) foot.append(controls);
  el.append(foot);
  return el;
}
